import AnalyticsUtils from '@daedalus/core/src/analytics/utils'
import {TRACK_EVENT_NEW} from 'modules/analytics/actions/trackEventNew'
import {getEventProperties} from 'modules/analytics/trackEventNewHelpers'

import {TRACK_PAGE_VIEW} from 'modules/analytics/actions/trackPageView'
import {LOG_EVENT} from 'modules/analytics/actions/logEvent'
import {TRACK_RAA_OFFER_CLICKED} from 'modules/analytics/actions/trackRAAOfferClicked'

import {
  TRACK_ERROR_TRIGGERED,
  TRACK_USER_ACTION
} from '@daedalus/core/src/analytics/modules/actions'

import {identifyAuthenticatedUser} from '@daedalus/core/src/auth/modules/slice'

import {customIdentify} from '@daedalus/core/src/analytics/modules/middleware/customIdentify'

import {
  getTraitsUser,
  getUserId,
  getUserTier,
  hasPlusTier
} from '@daedalus/core/src/auth/modules/selectors'

import {
  getDeviceId,
  setDeviceId
} from '@daedalus/core/src/user/modules/device/slice'

import {urlParams} from 'utils/urlParams'
import getAnonymousId from 'components/data/URLParams/selectors/getAnonymousId'

import {logEvent} from 'services/datadogLogger'
import {trackFullStoryEvent, getFullStoryObject} from 'services/fullstory'

import {MiddlewareType} from 'middlewares'
import Settings from 'Settings'

const HOTEL_PAGES_NAMESPACE = 'HotelPages'

const trackPageView = (pageName, meta) => {
  AnalyticsUtils.trackPageView(pageName, meta)
}

export const trackAll = (eventName, eventProperties) => {
  // In order to save FS subscription credits send generic top-level event name: 'HotelPages'
  // and add more descriptive name to the payload. See details
  // https://app.shortcut.com/findhotel/story/48096/change-event-naming-in-fullstory-for-hotelpages-events
  if (eventName.startsWith(HOTEL_PAGES_NAMESPACE)) {
    trackFullStoryEvent(HOTEL_PAGES_NAMESPACE, {
      ...eventProperties,
      name: eventName
    })
  } else {
    trackFullStoryEvent(eventName, eventProperties)
  }

  AnalyticsUtils.trackEvent(eventName, eventProperties)
  logEvent(eventName, eventProperties)
}

const middleware: MiddlewareType = store => next => async action => {
  if (!AnalyticsUtils.analyticsPlatformEnabled()) return next(action)

  const state = store.getState()

  switch (action.type) {
    case TRACK_EVENT_NEW: {
      const {event} = action
      const {name} = event
      const params = urlParams()
      const eventProperties = getEventProperties({
        ...event,
        urlParams: params
      })

      trackAll(name, eventProperties)
      break
    }

    case TRACK_PAGE_VIEW: {
      trackPageView(action.pageName, action.meta)

      getFullStoryObject()?.identify?.(action?.meta?.anonymousId, {
        pageName: action?.pageName
      })

      break
    }

    // Currently triggered in Checkout only when Optimizely datafile fails to load
    case TRACK_ERROR_TRIGGERED: {
      const {event} = action

      trackAll('ErrorTriggered', event)
      break
    }

    case TRACK_RAA_OFFER_CLICKED: {
      const {event} = action
      trackAll('OfferClicked', event)
      break
    }

    // Currently triggered in Checkout only when 'screenshot' action is displatched from Core
    // TODO (R&E) [2022-05-01]: Remove when 'screenshot' is migrated to tracking v2
    case TRACK_USER_ACTION: {
      const {
        params: {action: eventAction, category, label, meta}
      } = action

      const metadata = {
        action: eventAction,
        category,
        label,
        meta
      }

      trackAll('UserAction', metadata)
      break
    }

    case identifyAuthenticatedUser.type: {
      const cognitoUser = state?.authentication?.user
      const claims = action.payload
      const userId = getUserId(claims)
      const deviceId = getDeviceId(state)
      const anonymousId = getAnonymousId()
      const cognitoTraits = getTraitsUser(cognitoUser)
      const memberPlus = cognitoTraits
        ? hasPlusTier(cognitoTraits.tier)
        : hasPlusTier(getUserTier(claims))

      AnalyticsUtils.identify(userId, {
        deviceId,
        memberPlus,
        user: cognitoTraits
      })
      /* eslint-disable @typescript-eslint/no-implicit-any-catch */
      try {
        await customIdentify(
          anonymousId,
          userId,
          deviceId,
          Settings.get('REACT_APP_ANALYTICS_CUSTOMER_DATA_ENDPOINT'),
          Settings.get('REACT_APP_ANALYTICS_CUSTOMER_DATA_API_KEY'),
          cognitoTraits
        )
      } catch (error) {
        logEvent(
          'CustomIdentifyEventFailed',
          {
            errorType: error?.message
          },
          'info'
        )
      }
      /* eslint-enable @typescript-eslint/no-implicit-any-catch */

      break
    }

    case setDeviceId.type: {
      const {payload: deviceId} = action
      const anonymousId = getAnonymousId()
      const userId = state.authentication?.userId
      const cognitoTraits = getTraitsUser(state.authentication?.user)

      AnalyticsUtils.identify(userId, {deviceId, user: cognitoTraits})

      /* eslint-disable @typescript-eslint/no-implicit-any-catch */
      try {
        await customIdentify(
          anonymousId,
          userId,
          deviceId,
          Settings.get('REACT_APP_ANALYTICS_CUSTOMER_DATA_ENDPOINT'),
          Settings.get('REACT_APP_ANALYTICS_CUSTOMER_DATA_API_KEY'),
          cognitoTraits
        )
      } catch (error) {
        logEvent(
          'CustomIdentifyEventFailed',
          {
            errorType: error?.message
          },
          'info'
        )
      }
      /* eslint-enable @typescript-eslint/no-implicit-any-catch */

      break
    }

    case LOG_EVENT: {
      const {metric, meta, level} = action
      logEvent(metric, meta, level)
      break
    }

    default:
      break
  }

  return next(action)
}

export default middleware
